import React, { useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  IconButton,
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Tooltip,
  Toolbar,
  Typography,
  List,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditDialog from "../Dialogs/EditDialog";
import { checkInvalidField } from "../helpers/checkInvalidField";
import { getSchema } from "../helpers/schemas/getSchema";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import setupEdit from "../helpers/setupEdit";
import { getFormInvalid } from "../helpers/getFormInvalid";
import { onEditDeleteAll } from "../helpers/onEditDeleteAll";
import ChecklistTemplateCard from "./ChecklistTemplateCard";
import SortDialog from "../Dialogs/SortDialog";
import { getSortArray } from "../helpers/getSortArray";
import { castDateFields } from "../helpers/castDateFields";
import { castDateToFireStoreDate } from "../helpers/castDateToFireStoreDate";
import { firestoreAutoId } from "../helpers/firestoreAutoId";
import { saveToFirestore } from "../helpers/saveToFirestore";
import { getProject } from "../helpers/getProject";
import { getUserAccess } from "../helpers/getUserAccess";

export default function ChecklistTemplates(props) {
  // const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [checklists, setChecklists] = React.useState([]);
  const [allChecklists, setAllChecklists] = React.useState([]);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editFeature, setEditFeature] = React.useState(null);
  const [editSchema, setEditSchema] = React.useState([]);
  const [formErrorMessage, setformErrorMessage] = React.useState("");
  // const [importTitle, setImportTitle] = React.useState("New Checklist");
  const [filterValue, setFilterValue] = React.useState("");
  const [sortByValue, setSortByValue] = React.useState("Name");
  const [sortByOptions] = React.useState([
    "Name",
    "Name Desc",
    "Created Date",
    "Created Date Desc",
  ]);
  const [project, setProject] = React.useState({});
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log(props.user);
        const params = window.location.href.split("/");
        const projectId = params[params.length - 2];
        const proj = await getProject(projectId);
        onSortChange(sortByValue, proj.ChecklistTemplates);
        setAllChecklists(proj.ChecklistTemplates);
        setProject(proj);
      } catch (error) {
        console.error("Error getting project:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  }); 

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allChecklists.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });
      setChecklists([...filteredData]);
    } else {
      setChecklists([...allChecklists]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setChecklists([...obs]);
  };

  const onAddChecklist = () => {
    var item = {};
    var schema = getSchema("checklist");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["id"] = "new";

    item = applyEditTrackingFields(item, props.user);

    setEditSchema(schema);
    setEditFeature(item);
    setEditDialogOpen(true);
  };

  const onEdit = (id) => {
    var item = {};
    const results = setupEdit({ ...item }, "checklist", id);
    setIsLoading(false);
    setEditSchema(results.schema);
    setEditFeature({ ...results.item });
    setEditDialogOpen(true);
  };

  const onEditDelete = async () => {
    try {
      setIsLoading(true);
      const savedProj = await onEditDeleteAll(
        project,
        editFeature,
        [],
        "checklists",
        "checklists_id",
        props.user,
      );

      // props.saveCurrentProject({ ...savedProj });
      setProject({ ...savedProj });
      // setAllThechecklists(savedProj.checklists);
      enqueueSnackbar("Issue deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setEditDialogOpen(false);
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditSave = async () => {
    try {
      //check for invalid
      var feat = { ...editFeature };
      feat.rows = [];
      feat.uid = props.user.uid;
      var invalidResults = getFormInvalid(feat, editSchema);
      if (invalidResults.isInvalid === true) {
        setEditFeature({ ...invalidResults.feature });
        setEditSchema([...invalidResults.schema]);
        setformErrorMessage(invalidResults.formErrorMessage);
        return;
      }

      setIsLoading(true);

      const newFeat = castDateFields(feat, editSchema);
      const newEditFeature = castDateToFireStoreDate({ ...newFeat });
      if (newEditFeature.id === "new") {
        newEditFeature.id = firestoreAutoId();
        allChecklists.push(newEditFeature);
        setAllChecklists([...allChecklists]);
        checklists.push(newEditFeature);
        setAllChecklists([...checklists]);
      } else {
        const updatedArray1 = allChecklists.map((obj) => {
          if (obj.id === newEditFeature.id) {
            return newEditFeature; // Replace the object if its ID matches
          }
          return obj; // Keep other objects as they are
        });
        setAllChecklists([...updatedArray1]);
        const updatedArray2 = checklists.map((obj) => {
          if (obj.id === newEditFeature.id) {
            return newEditFeature; // Replace the object if its ID matches
          }
          return obj; // Keep other objects as they are
        });
        onSortChange(sortByValue, updatedArray2);
      }
      const updatedProject = { ...project };
      updatedProject.ChecklistTemplates = [...allChecklists];
      setProject(updatedProject);
      //Finally save the project
      await saveToFirestore("projects", updatedProject);
      setEditDialogOpen(false);
      enqueueSnackbar("Checklists saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditChange = (e) => {
    try {
      // console.log(e);
      var att = editSchema.find((item) => item.name === e.target.name);
      const formErrorMessage = checkInvalidField(e.target.value, att);
      if (att.type === "date") {
        if (e.target.value.length > 0) {
          var date = new Date(e.target.value);
          // date.setDate(date.getDate() + 1);
          editFeature[e.target.name] = date.toISOString().split("T")[0];
          // editFeature[e.target.name] = date.getTime();
          setEditFeature({ ...editFeature });
          setformErrorMessage(formErrorMessage);
        }
      } else if (att.type === "bit") {
        editFeature[e.target.name] = e.target.checked;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else {
        editFeature[e.target.name] = e.target.value;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditCancel = () => {
    setformErrorMessage("");
    setEditDialogOpen(false);
    // props.onEditClick();
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <Scrollbars
      autoHide
      autoHideTimeout={10}
      style={{ width: "100%", height: "100%" }}
    >
      <Toolbar />
      <Container
        maxWidth="xl"
        style={{
          // paddingTop: 30,
          paddingBottom: 30,
          // paddingBottom: theme.spacing(4),
          justifyContent: "center",
        }}
      >
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Checklist Templates
        </Typography>
        <Stack
          // style={{ paddingTop: 10 }}
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <Tooltip title="Add Checklist">
            <IconButton
              onClick={onAddChecklist}
              disabled={
                !getUserAccess(props.user, project.uid, project.Contacts)
                  .allowCreate
              }
              aria-label="Add Checklist"
              size="large"
              color="primary"
            >
              <AddBoxIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack
          style={{ paddingTop: 30 }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {allChecklists.length > 0 && (
            <SortDialog
              sortByValue={sortByValue}
              onSortChange={onSortChange}
              objArray={checklists}
              sortByOptions={sortByOptions}
              filterValue={filterValue}
              onFilterChange={onFilterChange}
              page={page}
              handleChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}

          {checklists.length === 0 ? (
            <Typography>
              There aren't any checklist templates associated with your account.
              Please add
              <IconButton
                onClick={onAddChecklist}
                aria-label="Add Checklist"
                disabled={
                  !getUserAccess(props.user, project.uid, project.Contacts)
                    .allowCreate
                }
                size="medium"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
              a new checklist template.
            </Typography>
          ) : (
            <List style={{ width: "100%" }}>
              <ChecklistTemplateCard
                checklists={checklists}
                projectId={project.id}
                page={page}
                rowsPerPage={rowsPerPage}
                onEdit={onEdit}
                // onDetailsClick={onDetailsClick}
                // handleChecked={handleChecked}
              />
            </List>
          )}
        </Stack>
        <EditDialog
          allowDeleteAccess={
            getUserAccess(props.user, project.uid, project.Contacts).allowDelete
          }
          feature={editFeature}
          editFiles={[]}
          importTitle={"New Checklist Template"}
          getAcceptedFiles={null}
          onEditFileCommentChange={null}
          onRemoveEditFile={null}
          schema={editSchema}
          onChange={onEditChange}
          onEditSave={onEditSave}
          onEditDelete={onEditDelete}
          isOpen={editDialogOpen}
          onClose={onEditCancel}
          formErrorMessage={formErrorMessage}
          allowDelete={false}
          allowFiles={false}
        />
      </Container>
    </Scrollbars>
  );
}
